import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { TrainingQuiz } from '../types';

export const useTrainingQuizzes = (moduleId?: string) => {
  const queryClient = useQueryClient();

  const { data: quizzes = [], isLoading } = useQuery({
    queryKey: ['training-quizzes', moduleId],
    queryFn: async () => {
      if (!moduleId) return [];
      
      const { data, error } = await supabase
        .from('training_quizzes')
        .select('*')
        .eq('module_id', moduleId)
        .order('order_position', { ascending: true });

      if (error) throw error;
      return data.map(q => ({
        ...q,
        options: q.options as string[],
      })) as TrainingQuiz[];
    },
    enabled: !!moduleId,
  });

  const createQuiz = useMutation({
    mutationFn: async (quiz: Omit<TrainingQuiz, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('training_quizzes')
        .insert(quiz)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-quizzes', moduleId] });
      toast.success('Pregunta agregada');
    },
    onError: (error) => {
      toast.error('Error al agregar pregunta: ' + error.message);
    },
  });

  const updateQuiz = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<TrainingQuiz> & { id: string }) => {
      const { data, error } = await supabase
        .from('training_quizzes')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-quizzes', moduleId] });
    },
  });

  const deleteQuiz = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('training_quizzes')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-quizzes', moduleId] });
      toast.success('Pregunta eliminada');
    },
  });

  const reorderQuizzes = useMutation({
    mutationFn: async (updates: { id: string; order_position: number }[]) => {
      // Update each quiz's order_position
      const promises = updates.map(({ id, order_position }) =>
        supabase
          .from('training_quizzes')
          .update({ order_position })
          .eq('id', id)
      );

      const results = await Promise.all(promises);
      const error = results.find((r) => r.error)?.error;
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-quizzes', moduleId] });
      toast.success('Orden actualizado');
    },
    onError: (error) => {
      toast.error('Error al reordenar: ' + error.message);
    },
  });

  return {
    quizzes,
    isLoading,
    createQuiz,
    updateQuiz,
    deleteQuiz,
    reorderQuizzes,
  };
};
